import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, Tag, ArrowLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';

const BlogPreviewPage = () => {
  const [previewData, setPreviewData] = useState<any>(null);
  
  useEffect(() => {
    // Load preview data from localStorage
    const data = localStorage.getItem('blog_preview');
    if (data) {
      try {
        setPreviewData(JSON.parse(data));
      } catch (error) {
        console.error('Error parsing preview data:', error);
      }
    }
  }, []);

  if (!previewData) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 pb-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">No Preview Available</h1>
            <p className="text-gray-600">Please go back and try again.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Format date to be more readable
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      timeZone: 'UTC'
    };
    return new Date(dateString + 'T00:00:00Z').toLocaleDateString('en-US', options);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={`Preview: ${previewData.title}`}
        description={previewData.excerpt}
        noIndex={true}
      />
      <Navbar />
      
      {/* Preview Banner */}
      <div className="pt-16 bg-yellow-100 border-b-2 border-yellow-300">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
              <span className="text-yellow-800 font-medium">Preview Mode</span>
            </div>
            <button
              onClick={() => window.close()}
              className="text-yellow-700 hover:text-yellow-900 transition-colors"
            >
              Close Preview
            </button>
          </div>
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="pt-8 pb-8 bg-gradient-to-br from-slate-50 via-ctc-silver-light to-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {previewData.title}
            </h1>
            
            <div className="flex flex-wrap items-center text-gray-500 mb-6 gap-4">
              <div className="flex items-center">
                <Calendar size={18} className="mr-2" />
                <span>{formatDate(previewData.date)}</span>
              </div>
              <div className="flex items-center">
                <User size={18} className="mr-2" />
                <span>{previewData.author}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="rounded-xl overflow-hidden"
          >
            <img 
              src={previewData.imageUrl} 
              alt={previewData.title} 
              className="w-full h-auto object-cover max-h-[500px]"
            />
          </motion.div>
        </div>
      </section>

      {/* Tags */}
      <section className="py-4 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <div className="flex flex-wrap gap-2 mb-8">
              {previewData.tags.map((tag: string) => (
                <span 
                  key={tag}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-ctc-blue/10 text-ctc-blue"
                >
                  <Tag size={14} className="mr-1" />
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="prose prose-lg max-w-none">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-gray-900 mt-8 mb-4" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-gray-900 mt-6 mb-3" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-xl font-bold text-gray-800 mt-5 mb-3" {...props} />,
                  h4: ({node, ...props}) => <h4 className="text-lg font-bold text-gray-800 mt-4 mb-2" {...props} />,
                  p: ({node, ...props}) => <p className="text-gray-700 mb-4 leading-relaxed" {...props} />,
                  a: ({node, ...props}) => <a className="text-ctc-blue hover:text-ctc-blue-dark transition-colors" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-4 text-gray-700" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-4 text-gray-700" {...props} />,
                  li: ({node, ...props}) => <li className="mb-1" {...props} />,
                  blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-ctc-blue pl-4 italic text-gray-700 my-4" {...props} />,
                  code: ({node, ...props}) => <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-800" {...props} />,
                  pre: ({node, ...props}) => <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto mb-4" {...props} />,
                }}
              >
                {previewData.content}
              </ReactMarkdown>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPreviewPage;